import { useRouteMatch } from 'react-router-dom';
import { Button } from 'foundations-components/transitional/components/Button';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { usePlaylistItemSearch } from './PlaylistItemSearchManager';
import PaginatedUICardGrid from '../../../containers/MyLearningPage/PaginatedUICardGrid';
import ApiError from '../../../components/Errors/ApiError';
import I18n from 'I18n';
import NoResultsError from '../../../components/Errors/NoResultsError';
import Raven from 'raven-js';
import { PLAYLIST_SEARCH_RESULTS_SIZE } from '../playlistItemsConsts';
import { useSearchFeedback } from '../../search/searchQueries';
import { useDisplayedData } from '../../table/tableHooks';
import { usePlaylistItemsSearch } from '../playlistItemsHooks';
import LearningContentCard from '../../cards/components/LearningContentCard';
import { View } from 'foundations-components/transitional/components/View';
import { AddPlaylistIcon, RemovePlaylistIcon } from './PlaylistAddRemoveButtons';
import { jsx as _jsx } from "react/jsx-runtime";
const AddRemovePlaylistSearchItem = ({
  onClick,
  label,
  icon
}) => /*#__PURE__*/_jsx(Button, {
  "aria-label": label,
  onClick: e => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  },
  use: "unstyled",
  children: icon
});
export const PlaylistItemSearchResults = ({
  onSearchPageChange
}) => {
  const {
    addPlaylistItem,
    removePlaylistItem,
    newPlaylistItems,
    resetQuery
  } = usePlaylistItemSearch();
  const [sendSearchFeedback] = useSearchFeedback();
  const {
    url
  } = useRouteMatch();
  const {
    isMobile
  } = useMediaQuery();
  const {
    results,
    loading,
    error,
    totalCount,
    page,
    setPage,
    searchCriteria,
    correlationId
  } = usePlaylistItemsSearch();
  const {
    results: displayedSearchResults,
    totalCount: displayedTotalCount = 0
  } = useDisplayedData({
    results,
    totalCount
  }, {
    loading,
    parameters: {
      page,
      searchCriteria
    }
  }) || {};
  if (error) {
    return /*#__PURE__*/_jsx(ApiError, {
      error: error,
      marker404: "CATALOG_COURSES_API_404",
      markerDefault: "CATALOG_COURSES_API_ERROR",
      resetLink: url
    });
  }
  const handleOnAddItemClick = ({
    type: contentContainerType,
    resultMLOptionId
  }) => {
    if (!correlationId) return; // only send feedback once per search and per item click

    sendSearchFeedback({
      variables: {
        correlationId,
        contentContainerType,
        resultMLOptionId
      }
    }).catch(Raven.captureException.bind(Raven));
  };
  const handlePageChange = newPage => {
    setPage(newPage);
    if (onSearchPageChange) onSearchPageChange();
  };
  if (!displayedSearchResults) return null;
  return displayedSearchResults.length ? /*#__PURE__*/_jsx(View, {
    marginBlockStart: isMobile ? '500' : '0',
    children: /*#__PURE__*/_jsx(PaginatedUICardGrid, {
      columnSize: 250,
      isLoadingPageOnDemand: true,
      limit: PLAYLIST_SEARCH_RESULTS_SIZE,
      onPageChange: handlePageChange,
      page: page,
      totalCount: displayedTotalCount,
      children: displayedSearchResults.map((item, index) => {
        const {
          id,
          type,
          language,
          name
        } = item;
        const {
          visibility
        } = item;
        const playlistItem = {
          itemId: Number(id),
          type,
          language: language === null || language === void 0 ? void 0 : language.toString().toLowerCase(),
          name,
          hasBeenRemoved: false,
          visibility: visibility || 'PUBLIC' // doesn't really matter, as items returned by the search are all visible to the user
        };
        const newPlaylistItem = newPlaylistItems.find(i => i.itemId === playlistItem.itemId && i.type === playlistItem.type);
        const isAdded = newPlaylistItem !== undefined && !(newPlaylistItem && newPlaylistItem.hasBeenRemoved);
        const addRemoveButton = /*#__PURE__*/_jsx(AddRemovePlaylistSearchItem, {
          icon: isAdded ? /*#__PURE__*/_jsx(RemovePlaylistIcon, {
            padding: "100",
            size: "400"
          }) : /*#__PURE__*/_jsx(AddPlaylistIcon, {
            padding: "100",
            size: "400"
          }),
          label: isAdded ? I18n.text('learningCenter.savedItems.playlistDetails.removeFromPlaylist') : I18n.text('learningCenter.savedItems.playlistDetails.addToPlaylist'),
          onClick: () => {
            if (isAdded) {
              removePlaylistItem(playlistItem);
            } else {
              handleOnAddItemClick(item);
              addPlaylistItem(playlistItem);
            }
          }
        });
        return /*#__PURE__*/_jsx(LearningContentCard, {
          "data-test-id": `${type === 'SHORT_FORM_CONTENT' ? 'short' : type === 'TRACK' ? 'track' : 'lesson'}-${id}`,
          isGhost: newPlaylistItem && newPlaylistItem.hasBeenRemoved,
          item: item,
          overviewType: "playlist-items-search-results",
          position: index + 1,
          renderMenu: () => addRemoveButton,
          showOverview: true
        }, `item_${id}`);
      })
    })
  }) : /*#__PURE__*/_jsx(NoResultsError, {
    onReset: resetQuery,
    resetLink: url
  });
};